import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from './Button.js';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Roter Bestätigen-Button für destruktive Aktionen. */
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Stilechter Ersatz für window.confirm(): `if (await confirm({...})) …` */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (fn === null) {
    throw new Error('ConfirmContext fehlt — ConfirmProvider in der Shell mounten.');
  }
  return fn;
}

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ options, resolve });
      }),
    [],
  );

  const close = (result: boolean): void => {
    pending?.resolve(result);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending !== null ? (
        <div
          className="au-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close(false);
          }}
        >
          <div
            className="au-dialog au-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-label={pending.options.title}
          >
            <h2 className="au-confirm__title">{pending.options.title}</h2>
            {pending.options.description !== undefined ? (
              <p className="au-confirm__text">{pending.options.description}</p>
            ) : null}
            <div className="au-confirm__actions">
              <Button variant="ghost" onClick={() => close(false)}>
                {pending.options.cancelLabel ?? 'Abbrechen'}
              </Button>
              <Button
                variant={pending.options.danger === true ? 'danger' : 'primary'}
                onClick={() => close(true)}
              >
                {pending.options.confirmLabel ?? 'Bestätigen'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}
